import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Certificate, QrCode, ArrowsLeftRight, Package, Plus, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/DashboardLayout";
import { PageHeader, Spinner, CategoryBadge } from "../components/ui-bits";
import { api, getMerchantId, getMerchantName, formatDate, CATEGORY_LABELS } from "../lib/api";

const StatCard = ({ icon: Icon, label, value, delay }) => (
  <div
    className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-7 hover:-translate-y-1 hover:shadow-luxe-lg transition-all duration-300 animate-fade-up"
    style={{ animationDelay: `${delay}s` }}
    data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
  >
    <div className="flex items-center justify-between">
      <div className="h-11 w-11 rounded-xl bg-navy/5 flex items-center justify-center">
        <Icon size={22} weight="light" className="text-navy" />
      </div>
    </div>
    <p className="mt-5 font-playfair text-4xl text-navy">{value}</p>
    <p className="text-[0.7rem] uppercase tracking-[0.18em] text-navy/45 mt-1">{label}</p>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const merchantId = getMerchantId();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/dashboard/stats?merchant_id=${merchantId}`);
      setStats(data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/products/sync", { merchant_id: merchantId });
      toast.success(`${data.synced} products synced`);
      load();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const cards = stats ? [
    { icon: Certificate, label: "Certificates Issued", value: stats.certificatesIssued },
    { icon: QrCode, label: "Verified Scans", value: stats.verifiedScans },
    { icon: ArrowsLeftRight, label: "Transfers Completed", value: stats.transfersCompleted },
    { icon: Package, label: "Products Synced", value: stats.productsSynced },
  ] : [];

  return (
    <DashboardLayout>
      <PageHeader
        overline={`Welcome back · ${getMerchantName()}`}
        title="Dashboard"
        subtitle="Your Digital Product Passport programme at a glance."
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-40 rounded-2xl bg-white/60 border border-navy/5 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c, i) => <StatCard key={c.label} {...c} delay={i * 0.05} />)}
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <button data-testid="quick-issue-btn" onClick={() => navigate("/products")} className="flex items-center gap-2 bg-navy text-cream rounded-full px-5 py-3 text-sm hover:brightness-110 active:scale-[0.99] transition-all">
          <Plus size={17} weight="bold" /> Issue Passport
        </button>
        <button data-testid="quick-sync-btn" onClick={sync} disabled={syncing} className="flex items-center gap-2 bg-white border border-navy/15 text-navy rounded-full px-5 py-3 text-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
          {syncing ? <Spinner className="h-4 w-4" /> : <ArrowsClockwise size={17} weight="light" />} Sync Products
        </button>
        <button data-testid="quick-view-btn" onClick={() => navigate("/passports")} className="flex items-center gap-2 bg-white border border-navy/15 text-navy rounded-full px-5 py-3 text-sm hover:border-gold hover:text-gold transition-colors">
          View All Passports <ArrowRight size={16} weight="bold" />
        </button>
      </div>

      <div className="mt-12">
        <h2 className="text-xl text-navy mb-5">Recent Passports</h2>
        {loading ? null : stats.recentDPPs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-navy/10 p-12 text-center text-navy/50">
            <Certificate size={40} weight="light" className="mx-auto text-navy/20" />
            <p className="mt-4 text-sm">No passports issued yet. Head to Products to issue your first one.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {stats.recentDPPs.map((d) => (
              <div key={d.certificateId} data-testid="recent-dpp-card" className="bg-white rounded-2xl border border-navy/10 shadow-luxe overflow-hidden hover:-translate-y-1 hover:shadow-luxe-lg transition-all duration-300">
                <div className="h-36 bg-cream overflow-hidden">
                  {d.image ? <img src={d.image} alt={d.productName} className="w-full h-full object-cover" /> : null}
                </div>
                <div className="p-5">
                  <CategoryBadge label={CATEGORY_LABELS[d.category] || d.category} />
                  <p className="mt-2 text-navy font-medium truncate">{d.productName}</p>
                  <p className="text-xs text-navy/40 mt-1">{d.certificateId}</p>
                  <p className="text-xs text-navy/40">Issued {formatDate(d.issuedAt)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
